import React, { useState } from "react";
import { createPortal } from "react-dom";
import { ReactComponent as DeleteIcon } from "../../assets/icons/svg/delete-icon.svg";
import { ReactComponent as FeedbackIcon } from "../../assets/icons/svg/feedback_icon.svg";
import { ReactComponent as PencilIcon } from "../../assets/icons/svg/pencil-icon.svg";
import { ReactComponent as DeveloperIcon } from "../../assets/icons/svg/sheardevloper_icon.svg";
import { X } from "lucide-react";
import Button from "./buttons";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";
interface DraftCardProps {
  imageUrl: string;
  title: string;
  feedbackCount: number;
  onEdit: () => void;
  onShare: () => void;
  onDelete: () => void;
  showPlaceholder?: boolean;
  ownerName?: string | null;
  ownerProfile?: string | null;
  lastUpdated?: string;
}

const getInitials = (name: string | null | undefined): string => {
  if (!name) return "U";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const DraftCard: React.FC<DraftCardProps> = ({
  imageUrl,
  title,
  feedbackCount,
  onEdit,
  onShare,
  onDelete,
  showPlaceholder = false,
  ownerName,
  ownerProfile,
  lastUpdated,
}) => {
  const [imageError, setImageError] = useState(false);
  const [ownerImgError, setOwnerImgError] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Determine if we should show placeholder
  const shouldShowPlaceholder =
    showPlaceholder ||
    !imageUrl ||
    imageUrl.includes("placeholder") ||
    imageUrl.includes("via.placeholder.com") ||
    imageError;

  const handleImageError = () => {
    console.error(`Failed to load image: ${imageUrl}`);
    setImageError(true);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    onDelete();
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const PlaceholderContent = () => (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center border-b border-gray-200">
      <div className="text-center">
        <svg
          className="w-8 h-8 text-gray-400 mx-auto mb-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <p className="text-xs text-gray-500">
          {feedbackCount === 0 ? "No screenshot yet" : "Screenshot loading..."}
        </p>
      </div>
    </div>
  );

  return (
  <>
    <div
      onClick={onEdit}
      className="rounded-xl border border-blue-200 overflow-hidden max-w-sm bg-white cursor-pointer hover:shadow-lg transition-shadow flex flex-col"
      style={{
        touchAction: 'pan-y pan-x',
        userSelect: 'none'
      }}
    >
        {/* Top Image Section */}
        <div className="h-44 overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-50">
          {!shouldShowPlaceholder ? (
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-full object-cover"
              onError={handleImageError}
              onLoad={() => setImageError(false)}
            />
          ) : (
            <PlaceholderContent />
          )}
        </div>

        {/* Content */}
        <div className="p-4 flex flex-col flex-1">
          {/* Title + Owner Avatar */}
          <div className="flex flex-col gap-2 mb-2">
            <h2
              className="text-lg font-semibold text-gray-900 truncate"
              title={title}
              style={{ fontFamily: DEFAULT_FONT_FAMILY }}
            >
              {title}
            </h2>

            {/* Owner Avatar */}
            {ownerName && (
              <div
                className="flex items-center"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Count Badge */}
                <div className="relative w-6 h-6">
                  <div
                    className="relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-[#1E1F26] text-white text-[10px] font-semibold border-[2px] border-white"
                  
                  >
                    1
                  </div>
                </div>
                {/* Owner Avatar */}
                <div
                  className="relative"
                  style={{ marginLeft: "-16px", zIndex: 1 }}
                >
                  <div className="w-6 h-6 rounded-full overflow-hidden flex items-center justify-center border-[2px] border-white">
                    {ownerProfile && !ownerImgError ? (
                      <img
                        src={ownerProfile}
                        alt={ownerName}
                        className="w-full h-full object-cover"
                        onError={() => setOwnerImgError(true)}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white font-semibold text-xs">
                        {getInitials(ownerName)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Fixed height spacer to match FeedbackCard badge container */}
          <div className="h-5 mb-1" />

          {/* Bottom Section */}
          <div className="mt-0">
            <div className="flex justify-between text-sm mb-3">
              <div
                className="flex items-center text-black gap-1"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                <FeedbackIcon className="w-4 h-4" />
                {feedbackCount} feedback{feedbackCount !== 1 ? "s" : ""}
              </div>
              {lastUpdated && (
                <div
                  className="text-gray-500"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  {lastUpdated}
                </div>
              )}
            </div>

            <div className="border-t border-blue-100" />

            <div className="pt-3 flex items-center justify-between">
              <span
                className="px-3 py-1 rounded-full text-xs font-medium bg-[#1920281A] text-[#192028]"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Draft
              </span>

              <div className="flex items-center gap-2 ml-auto">
                <button
                  className="text-red-500 hover:text-red-700 transition p-1 rounded"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick();
                  }}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  title="Delete draft"
                >
                  <DeleteIcon className="w-5 h-5 3xl:w-6 3xl:h-6" />
                </button>
                <Button
                  variant={feedbackCount === 0 ? "secondary" : "primary"}
                  disabled={feedbackCount === 0}
                  className="flex items-center text-xs 3xl:text-xs rounded-[4px] font-normal px-3 py-1.5 gap-1 disabled:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare();
                  }}
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  title={
                    feedbackCount === 0
                      ? "Add feedback before sharing"
                      : "Share with developer"
                  }
                >
                  Share file
                  {/* <DeveloperIcon className="w-4 h-4" /> */}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Popup */}
      {showDeleteConfirm &&
        createPortal(
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg shadow-xl max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-lg 2xl:max-w-xl 4xl:max-w-2xl w-full mx-4">
              <div className="mx-4 sm:mx-4 xl:mx-6 2xl:mx-6 4xl:mx-12 py-3 sm:py-3 xl:py-4 2xl:py-4 4xl:py-6">
                {/* Header */}
                <div className="flex items-center justify-between mt-4 mb-4">
                  <h3
                    className="text-[18px] 3xl:text-[20px] font-medium text-[#070707] text-left"
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Deleting file?
                  </h3>
                  
                </div>

                {/* Separator */}
                <div className="border-t border-gray-200 mb-4" />

                {/* Description */}
                <p
                  className="text-[#070707] text-sm 3xl:text-[16px] mb-4"
                  style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                >
                  Are you sure you want to delete this draft? This action cannot
                  be undone.
                </p>

                {/* Separator */}
                <div className="border-t border-gray-200 mb-6" />

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <Button
                    variant="cancel"
                    size="lg"
                    className="w-full sm:w-auto text-sm"
                    onClick={handleDeleteCancel}
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="danger"
                    size="lg"
                    className="w-full sm:w-auto text-sm"
                    onClick={handleDeleteConfirm}
                    style={{ fontFamily: DEFAULT_FONT_FAMILY }}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default DraftCard;
